import React, { ReactElement, useEffect } from 'react';
import { Button, Link, LoaderButton } from 'idsk-react-components';
import './detailTerminuActions.scss';
import { TerminDetail } from 'tezaury-frontend-ts-axios';
import { ConfirmModal } from 'slovlex-idsk-react-components';
import { useClient, useClientStatusContext } from '../../../context/ClientStatusContext';
import { changePublikacia } from '../../../client/termin/terminPublikaciaChangeClient';
import routes from '../../../routing/routes';
import TerminDeleteConfirmModal from '../component/TerminDeleteConfirmModal';
import {
  exportTerminDetail,
  exportTerminDetailAdmin,
} from '../../../client/export/exportDetailClient';
import downloadFile from '../../../functions/downloadFile';
import { technickePricinyErrorStatus } from '../../../constants/ErrorConstant';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import scrollToTop from '../../../functions/scrollToTop';

interface Props {
  termin: TerminDetail;
  admin?: boolean;
  reloadTermin;
}

const DetailTerminuActions = ({
  termin,
  admin,
  reloadTermin,
}: Props): ReactElement => {
  const { displaySuccess } = useSuccessMessageContext();
  const {
    wrapClient: changePublikaciaClient,
    status: changePublikaciaStatus,
  } = useClient(changePublikacia);
  const { handleStatus } = useClientStatusContext();
  const {
    wrapClient: exportTermin,
    status: exportTerminStatus,
  } = useClient(exportTerminDetail);

  const {
    wrapClient: exportTerminAdmin,
    status: exportTerminAdminStatus,
  } = useClient(exportTerminDetailAdmin);

  useEffect(() => {
    if (exportTerminStatus?.responseCode === 404 || changePublikaciaStatus?.responseCode === 404
        || exportTerminAdminStatus?.responseCode === 404) {
      handleStatus(technickePricinyErrorStatus);
    }
  }, [exportTerminStatus, changePublikaciaStatus, exportTerminAdminStatus, handleStatus]);

  const onExportToPdf = async (e): Promise<void> => {
    e.preventDefault();
    const { data } = await exportTermin(termin.uuid);

    if (data.blob) {
      downloadFile(data.fileName, data.blob);
    }
  };

  const onExportToPdfAdmin = async (e): Promise<void> => {
    e.preventDefault();
    const { data } = await exportTerminAdmin(termin.uuid);

    if (data.blob) {
      downloadFile(data.fileName, data.blob);
    }
  };

  const changePublikaciaButton = (
    publikovany: boolean,
    openButtonText: string,
    confirmButtonStyle: 'action' | 'negative',
    confirmModalQuestion: string,
    confirmModalDescription: string,
  ): ReactElement => (
    <ConfirmModal
      question={confirmModalQuestion}
      description={confirmModalDescription}
      cancelButtonText="Zrušiť"
      confirmButton={
            (
              <Button buttonStyle={confirmButtonStyle} size="large">{openButtonText}</Button>
            )
          }
      openButton={(
        <Button
          buttonStyle="third"
          size="large"
        >
          {openButtonText}
        </Button>
          )}
      doAfterConfirm={async () => {
        await changePublikaciaClient(termin.uuid, publikovany);
        reloadTermin();
        displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
        scrollToTop();
      }}
    />
  );

  if (admin) {
    return (
      <div className="detail-terminu-actions">
        <LoaderButton
          loading={exportTerminAdminStatus.loading}
          buttonStyle="third"
          size="large"
          type="button"
          onClick={(event) => onExportToPdfAdmin(event)}
        >Exportovať do PDF
        </LoaderButton>
        <Link
          to={routes.tezaurus.admin.termin.historiaZmien.withParams(termin.tezaurus.uuid, termin.uuid)}
          className="termin-list__add"
          type="button-third"
          size="large"
        >
          História zmien
        </Link>
        {!termin.publikovany && (
          changePublikaciaButton(
            true,
            'Publikovať',
            'action',
            `Naozaj si prajete publikovať termín "${termin.preferovanyTermin}"?`,
            'Po publikovaní bude termín dostupný pre verejnosť.',
          )
        )}
        {termin.publikovany && (
          changePublikaciaButton(
            false,
            'Zrušiť publikovanie',
            'negative',
            `Naozaj si prajete zrušiť publikovanie termínu "${termin.preferovanyTermin}"?`,
            'Po zrušení publikovania už termín nebude dostupný pre verejnosť.',
          )
        )}
        <TerminDeleteConfirmModal
          termin={{
            uuid: termin.uuid,
            publikovany: termin.publikovany,
            termin: termin.preferovanyTermin,
          }}
          buttonStyle="negative"
          buttonSize="large"
          openButtonText="Zmazať termín"
        />
      </div>
    );
  }
  return (
    <div className="detail-terminu-actions">
      <LoaderButton
        loading={exportTerminStatus.loading}
        size="large"
        buttonStyle="third"
        type="button"
        onClick={(event) => onExportToPdf(event)}
      >Exportovať do PDF
      </LoaderButton>
    </div>
  );
};

export default DetailTerminuActions;
